import React, { useCallback, useMemo, useState } from 'react';
import {
  DatePicker,
  Form,
  FormInstance,
  notification,
  Popconfirm,
  Tabs,
} from 'antd';
import DF from 'DisplayFramework';
import { indexBy, setValueByNamePath } from 'utils/utils2';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { ParAssetCatalogServiceResponse } from 'services/assetconfig';
import { useFormList } from 'hooks/useFormList';
import DeleteButton from 'components/DeleteButton';
import Title from 'components/Title';
import InputRate from 'components/InputRate';
import InputMoney from 'components/InputMoney';
import {
  addAssetService,
  deleteAssetService,
  updateAssetService,
} from 'services/assets';
import ButtonOperation from 'components/ButtonOperation';
import ServiceList from 'components/ServiceList';
import { PlusOutlined } from '@ant-design/icons';
import SaveButton from 'components/SaveButtonAction';
import ModalControllerNV from 'components/ModalControllerNV';
import ModalAssetServiceDetail from '../AssetDetail/AssetServiceDetail';
import moment from 'moment';
import TabPane from 'antd/lib/tabs/TabPane';
import AssetServiceUseListByAssetid from '../AssetServiceUseListByAssetid';

const NEW_SERVICE_ID = -1;

type AssetServicesProps = {
  asset: any;
  assetid: any;
  form: FormInstance;
  refetchassetServices: any;
  isFetching: boolean;
};

const AssetServices = (props: AssetServicesProps) => {
  const { asset, assetid, form, refetchassetServices, isFetching } = props;

  const fieldName = 'assetServiceList';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'associd',
    addAsFirst: true,
  });

  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: [],
    });
  }

  const columns = useMemo<DataGridColDef<ParAssetCatalogServiceResponse>[]>(
    () => [
      {
        dataIndex: 'servlabel',
        render: (_value, _record, index) =>
          _record.associd === -1 ? (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'associd')[
                  _record.associd
                ],
                'servcode',
              ]}
              noStyle
            >
              <ServiceList
                style={{ width: '100%' }}
                servtype="SERV"
                onSelected={(e) => {
                  setValueByNamePath(
                    form,
                    [
                      fieldName,
                      indexBy(form.getFieldValue(fieldName), 'associd')[
                        _record.associd
                      ],
                      'servpercentage',
                    ],
                    e?.servrepaypercentage ?? 0
                  );

                  setValueByNamePath(
                    form,
                    [
                      fieldName,
                      indexBy(form.getFieldValue(fieldName), 'associd')[
                        _record.associd
                      ],
                      'servfixedamount',
                    ],
                    e?.servfixedamount ?? 0
                  );
                }}
              />
            </Form.Item>
          ) : (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <ModalAssetServiceDetail
                  record={_record}
                  onCancel={() => setVisible(false)}
                  visible={visible}
                />
              )}
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>{_record?.servlabel}</a>
              )}
            />
          ),
        width: 180,
      },
      {
        dataIndex: 'startdate',
        render: (_value, _record) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'associd')[
                  _record.associd
                ],
                'startdate',
              ]}
              noStyle
            >
              <DatePicker />
            </Form.Item>
          );
        },
        width: 180,
      },
      {
        dataIndex: 'enddate',
        render: (_value, _record) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'associd')[
                  _record.associd
                ],
                'enddate',
              ]}
              noStyle
            >
              <DatePicker />
            </Form.Item>
          );
        },
        width: 180,
      },
      {
        dataIndex: 'servpercentage',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'associd')[
                  _record.associd
                ],
                'servpercentage',
              ]}
              noStyle
            >
              <InputRate style={{ width: '100%' }} precision={2} />
            </Form.Item>
          );
        },
        width: 180,
      },
      {
        dataIndex: 'servfixedamount',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'associd')[
                _record.associd
              ],
              'servfixedamount',
            ]}
            noStyle
          >
            <InputMoney style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'status',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'associd')[
                _record.associd
              ],
              'status',
            ]}
            noStyle
          >
            {_record.status}
          </Form.Item>
        ),
        width: 180,
        fixed: 'right',
      },
      {
        key: 'actions',
        render: (_, _record) =>
          _record.associd === NEW_SERVICE_ID ? (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                size="small"
                comcode="save"
              />
              <DeleteButton onlyIcon onClick={() => remove(_record.associd)} />
            </>
          ) : (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                size="small"
                comcode="update"
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteAssetService({
                    associd: _record.associd,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetchassetServices();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: 180,
        fixed: 'right',
      },
    ],
    [form, refetchassetServices]
  );

  const addRow = () => {
    const row: any = {
      associd: NEW_SERVICE_ID,
      assetid: assetid,
      servcode: '',
      servfixedamount: 0,
      servpercentage: 0,
    };
    add(row);
  };

  const onSave = useCallback(
    (record: any) => {
      const steps: any[] = form.getFieldValue(fieldName);
      const index = indexBy(steps, 'associd')[record.associd];
      const currentValue = steps[index];
      const { associd } = currentValue;
      associd === NEW_SERVICE_ID
        ? addAssetService({
            request: {
              ...currentValue,
              enddate: moment(currentValue.enddate, 'YYYY-MM-DD'),
              startdate: moment(currentValue.startdate, 'YYYY-MM-DD'),
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetchassetServices();
            }
          })
        : updateAssetService({
            request: {
              ...currentValue,
              enddate: moment(currentValue.enddate, 'YYYY-MM-DD'),
              startdate: moment(currentValue.startdate, 'YYYY-MM-DD'),
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetchassetServices();
            }
          });
    },
    [form, refetchassetServices]
  );
  const [activeKey, setActiveKey] = useState('1');

  return (
    <DF.Provider pagcode="assetCatalogServices">
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const services: ParAssetCatalogServiceResponse[] =
              getFieldValue(fieldName);

            const isNewItemInTable =
              services?.find(({ associd }) => associd === NEW_SERVICE_ID) !==
              undefined;
            return (
              <Tabs
                onChange={(activeKey) => setActiveKey(activeKey)}
                type="card"
              >
                <TabPane key="1" tab="AssetServiceDetail">
                  <DataGrid
                    rowKey="associd"
                    columns={columns}
                    dataSource={getFieldValue(fieldName)}
                    title={<Title level={3} comcode="assetCatalogServices" />}
                    hideFilters
                    isLoading={isFetching}
                    extraActions={
                      <ButtonOperation
                        type="primary"
                        disabled={isNewItemInTable || assetid === 0}
                        icon={<PlusOutlined />}
                        onClick={addRow}
                        comcode="add"
                      />
                    }
                  />
                </TabPane>
                <TabPane tab="HisthoriqueAssetService" key="2">
                  <AssetServiceUseListByAssetid assetid={props.assetid} />
                </TabPane>
              </Tabs>
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default AssetServices;
